// Sprinter — liste, opprett, rediger, fremdrift.
//
// DET ENE SOM ER VIKTIGERE ENN ALT ANNET PÅ DENNE SKJERMEN:
// «Tid gått» og «arbeid ferdig» tegnes som TO søyler, med hvert sitt tall og
// hver sin farge, aldri som én. Kontrakten sier hvorfor: en sprint kan være 90 %
// gjennom i tid og 20 % i arbeid, og nettopp det avviket er funnet. Slår man dem
// sammen, forsvinner det eneste sprintvisningen egentlig er til for å oppdage.
// Under de to søylene står én setning som sier hva forskjellen betyr.
import type { Kontekst } from './kontekst.js';
import type { SprintKropp } from '../api/typer.js';
import {
  normaliserSprint, sorterSprinter, tidMotArbeid, validerSprint, TILSTAND_NAVN,
  type SprintVisning,
} from '../lib/sprint-lib.js';
import { formaterDatoMedÅr, somDatoFelt, tolkØktSkjema } from '../lib/tid.js';
import { div, felt, h, knapp, merke, p, sett, span, tekstområde, velger } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, søyle, tomt } from '../ui/tilstand.js';

const TILSTANDSVALG = (['planlagt', 'aktiv', 'ferdig', 'avbrutt'] as const)
  .map((v) => ({ verdi: v, navn: TILSTAND_NAVN[v] }));

const DØGN = 24 * 60 * 60 * 1000;

export function visSprinter(rot: HTMLElement, ctx: Kontekst): void {
  let sprinter: SprintVisning[] = [];

  const side = div('side');
  sett(rot, side);

  const topp = div('side-topp');
  const titler = div('');
  titler.append(h(1, 'side-tittel', 'Sprinter'));
  titler.append(p('side-under', 'Tid som er gått og arbeid som er gjort er to forskjellige '
    + 'tall. Begge vises, hver for seg.'));
  topp.append(titler);
  const knapper = div('filterrad');
  knapper.append(knapp('Ny sprint', () => visSkjema(null), { variant: 'primær', ikon: ikon('pluss') }));
  topp.append(knapper);
  side.append(topp);

  const varselplass = div('');
  side.append(varselplass);

  const skjemaplass = div('');
  side.append(skjemaplass);

  const listeplass = div('');
  side.append(listeplass);

  void hent();

  async function hent(): Promise<void> {
    sett(listeplass, laster('Henter sprinter …', 3));
    const r = await ctx.klient.sprinter();
    if (!r.ok) return sett(listeplass, feilboks(r.feil, () => void hent()));
    const rader = r.data?.sprinter ?? [];
    sprinter = sorterSprinter(
      rader.map(normaliserSprint).filter((s): s is SprintVisning => s !== null),
    );
    // `advarsler` er alltid med i svaret og er tom når alt er bra — det er
    // INNHOLDET som betyr noe, ikke at feltet finnes.
    tegnVarsler(Array.isArray(r.data?.advarsler) ? r.data.advarsler : [], rader.length);
    tegn();
  }

  function tegnVarsler(advarsler: readonly string[], antallRader: number): void {
    sett(varselplass);
    const forkastet = antallRader - sprinter.length;
    const linjer = [...advarsler];
    if (forkastet > 0) {
      // Rader vi ikke klarte å lese skal ikke bare forsvinne ut av lista.
      linjer.push(`${forkastet} rad(er) fra tjenesten manglet id eller navn og vises ikke.`);
    }
    for (const a of linjer) {
      const boks = div('advarsel advarsel-mild');
      boks.setAttribute('role', 'status');
      const hode = div('advarsel-hode');
      hode.append(ikon('info'), h(2, 'advarsel-tittel', 'Merk'));
      boks.append(hode);
      boks.append(p('advarsel-linje', a));
      varselplass.append(boks);
    }
  }

  function tegn(): void {
    sett(listeplass);
    if (!sprinter.length) {
      listeplass.append(tomt('Ingen sprinter ennå',
        'En sprint samler kort i en avgrenset periode, så staben kan se hva som skal '
        + 'være ferdig når. Lag den første.',
        { ikon: 'sprint', handling: { tekst: 'Ny sprint', gjør: () => visSkjema(null) } }));
      return;
    }
    for (const s of sprinter) listeplass.append(sprintkort(s));
  }

  function sprintkort(s: SprintVisning): HTMLElement {
    const kort = div(`kort sprintkort sprint-${s.tilstand}`);

    const hode = div('kort-hode');
    hode.append(ikon('sprint'));
    hode.append(h(2, 'kort-tittel', s.navn));
    hode.append(merke(TILSTAND_NAVN[s.tilstand], tilstandsvariant(s.tilstand)));
    const rom = div('kort-hode-rom');
    hode.append(rom);
    hode.append(knapp('Endre', () => visSkjema(s), { variant: 'stille' }));
    hode.append(knapp('Slett', () => void slett(s), { variant: 'fare' }));
    kort.append(hode);

    if (s.mal) kort.append(p('sprint-mal', s.mal));

    const periode = div('sprint-periode');
    periode.append(ikon('kalender'));
    periode.append(span('', `${formaterDatoMedÅr(s.startTsMs)} – ${formaterDatoMedÅr(s.sluttTsMs)}`));
    periode.append(span('sprint-dager', s.tilstand === 'aktiv'
      ? `${s.dagerIgjen} av ${s.dagerTotalt} dager igjen`
      : `${s.dagerTotalt} dager`));
    kort.append(periode);

    const t = tidMotArbeid(s.tidsandel, s.fremdrift.andel);

    // ── De to søylene. Merk at de har hver sin etikett, hver sin verdi og
    // hver sin farge, og at de ALDRI legges oppå hverandre.
    const måling = div('måling');
    måling.append(målelinje(
      'Tid gått', t.tidsandel, 'tid',
      `${Math.round(t.tidsandel * 100)} %`,
      s.tilstand === 'aktiv' ? `${s.dagerIgjen} dager igjen` : `${s.dagerTotalt} dager totalt`,
    ));
    måling.append(målelinje(
      'Arbeid ferdig', t.arbeidsandel, 'arbeid',
      `${Math.round(t.arbeidsandel * 100)} %`,
      `${s.fremdrift.ferdig} av ${s.fremdrift.totalt} kort · ${s.fremdrift.paagaar} pågår`,
    ));
    kort.append(måling);

    const dom = div(`takt takt-${t.takt.replace(' ', '-')}`);
    dom.append(ikon(t.takt === 'bak' ? 'varsel' : 'info'));
    dom.append(span('', t.forklaring));
    kort.append(dom);

    const fot = div('sprint-fot');
    fot.append(span('sprint-antall', `${s.antallKort} kort i sprinten`));
    fot.append(knapp('Vis kortene', () => ctx.gåTil('kanban'), { variant: 'lenke', ikon: ikon('kanban') }));
    kort.append(fot);
    return kort;
  }

  function målelinje(etikett: string, andel: number, variant: string, tall: string, under: string): HTMLElement {
    const rad = div('målelinje');
    const topp2 = div('målelinje-topp');
    topp2.append(span('målelinje-etikett', etikett));
    topp2.append(span('målelinje-tall', tall));
    rad.append(topp2);
    rad.append(søyle(andel, variant, `${etikett}: ${tall}`));
    rad.append(span('målelinje-under', under));
    return rad;
  }

  // ══ Skjema ════════════════════════════════════════════════════════════
  function visSkjema(s: SprintVisning | null): void {
    const kort = div('kort kort-skjema');
    const hode = div('kort-hode');
    hode.append(ikon(s ? 'grep' : 'pluss'));
    hode.append(h(2, 'kort-tittel', s ? `Endre «${s.navn}»` : 'Ny sprint'));
    kort.append(hode);

    const skjema = document.createElement('form');
    skjema.className = 'skjema-rutenett';
    skjema.noValidate = true;

    const nå = Date.now();
    const navn = felt('Navn', { påkrevd: true, verdi: s?.navn ?? '', plassholder: 'f.eks. Bølge 141' });
    const start = felt('Start', { type: 'date', verdi: somDatoFelt(s?.startTsMs ?? nå) });
    const slutt = felt('Slutt', { type: 'date', verdi: somDatoFelt(s?.sluttTsMs ?? nå + 13 * DØGN) });
    const tilstand = velger('Tilstand', TILSTANDSVALG, s?.tilstand ?? 'planlagt');
    const mal = tekstområde('Mål — hva skal være annerledes når sprinten er ferdig', '', 3);
    if (s?.mal) mal.ta.value = s.mal;

    const feilEl = div('skjema-feil');
    feilEl.setAttribute('role', 'alert');
    feilEl.hidden = true;

    const lagre = knapp(s ? 'Lagre endringer' : 'Opprett sprint', undefined, { variant: 'primær', type: 'submit' });
    const avbryt = knapp('Avbryt', () => sett(skjemaplass), { variant: 'stille' });
    const fot = div('skjema-fot');
    fot.append(avbryt, lagre);

    skjema.append(navn.rot, start.rot, slutt.rot, tilstand.rot);
    const bred = div('skjema-bred');
    bred.append(mal.rot);
    skjema.append(bred, fot, feilEl);
    kort.append(skjema);
    sett(skjemaplass, kort);
    navn.input.focus();
    kort.scrollIntoView({ block: 'nearest' });

    skjema.addEventListener('submit', (e) => { e.preventDefault(); void send(); });

    async function send(): Promise<void> {
      for (const f of [navn, start, slutt]) f.settFeil(null);
      feilEl.hidden = true;

      // Sprintdatoer er DAGER, ikke klokkeslett: start ved norsk midnatt, slutt
      // ved slutten av den valgte dagen. En sprint som slutter kl. 00:00 på
      // sluttdatoen ville vært en dag kortere enn den brukeren valgte.
      const s0 = dagStart(start.input.value);
      const s1 = dagSlutt(slutt.input.value);
      const utkast = { navn: navn.input.value.trim(), mal: mal.ta.value, startTsMs: s0, sluttTsMs: s1 };
      const galt = validerSprint(utkast);
      if (galt) {
        const mål = { navn, start, slutt }[galt.felt as 'navn' | 'start' | 'slutt'];
        if (mål) { mål.settFeil(galt.grunn); mål.input.focus(); } else {
          feilEl.hidden = false;
          feilEl.textContent = galt.grunn;
        }
        return;
      }

      const kropp: SprintKropp = {
        navn: utkast.navn,
        mal: utkast.mal,
        startTsMs: utkast.startTsMs,
        sluttTsMs: utkast.sluttTsMs,
        status: tilstand.select.value as SprintKropp['status'],
      };
      lagre.disabled = true;
      const r = s ? await ctx.klient.endreSprint(s.id, kropp) : await ctx.klient.nySprint(kropp);
      lagre.disabled = false;
      if (!r.ok) {
        feilEl.hidden = false;
        feilEl.textContent = r.feil.melding;
        return;
      }
      sett(skjemaplass);
      ctx.kvitter(s ? 'Sprinten er oppdatert' : 'Sprinten er opprettet');
      ctx.kunngjør(`${kropp.navn} ${s ? 'oppdatert' : 'opprettet'}.`);
      await hent();
    }
  }

  async function slett(s: SprintVisning): Promise<void> {
    if (!globalThis.confirm(
      `Slette sprinten «${s.navn}»?\n\nKortene i sprinten slettes IKKE — de mister bare koblingen.`,
    )) return;
    const r = await ctx.klient.slettSprint(s.id);
    if (!r.ok) return ctx.kvitter(r.feil.melding, 'feil');
    ctx.kvitter('Sprinten er slettet');
    await hent();
  }
}

function tilstandsvariant(t: SprintVisning['tilstand']): string {
  return t === 'aktiv' ? 'gull' : t === 'ferdig' ? 'sage' : t === 'avbrutt' ? 'fare' : '';
}

/** Norsk midnatt på datoen. Faller tilbake til NaN, som `validerSprint` fanger. */
function dagStart(dato: string): number {
  const r = tolkØktSkjema(dato, '00:00', '00:01');
  return r.ok ? r.verdi.startTsMs : NaN;
}

/** Slutten av den norske dagen (23:59 + ett minutt = neste midnatt). */
function dagSlutt(dato: string): number {
  const r = tolkØktSkjema(dato, '23:59', '00:00');
  return r.ok ? r.verdi.startTsMs + 60_000 : NaN;
}
